import { useEffect, useState } from "react";
import { fetchContent } from "../../api/content";
import {
  completeGrow,
  completeRunIt,
  fetchGrow,
  fetchGrowLibrary,
  fetchRunIt,
  saveGrow,
  saveRunIt,
  toggleLibraryModule,
  type Budget,
  type GrowLibrary,
  type GrowView,
  type GrowthPlan,
  type RunItData,
  type RunItView,
} from "../../api/growth";
import { Banner } from "../../components/Banner";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { registerDraftSender, saveOrQueue } from "../../lib/offlineSync";
import { useAsync } from "../../lib/useAsync";
import { useDebouncedCallback } from "../../lib/useDebounced";
import { GrowLibraryScreen } from "../../screens/growth/GrowLibraryScreen";
import { GrowScreen } from "../../screens/growth/GrowScreen";
import { RunItScreen } from "../../screens/growth/RunItScreen";
import { DoneScreen } from "./DoneScreen";

registerDraftSender<RunItData>("runIt", saveRunIt);
registerDraftSender<{ budget: Budget; plan: GrowthPlan }>("grow", ({ budget, plan }) =>
  saveGrow(budget, plan),
);

export function RunItRoute() {
  const loaded = useAsync(() => Promise.all([fetchRunIt(), fetchContent()]), []);
  const [view, setView] = useState<RunItView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (loaded.data) setView(loaded.data[0]);
  }, [loaded.data]);

  const saveSoon = useDebouncedCallback((data: RunItData) => {
    void saveOrQueue("runIt", "runIt", data, async (value) =>
      setView(await saveRunIt(value)),
    ).catch((e: unknown) => setError(messageOf(e)));
  }, 400);

  if (finished) {
    return (
      <DoneScreen
        kicker="Step 5 complete"
        title="You're running client work like a professional."
        sub="One step left: hold on to the money you're making and plan your next 90 days."
        next={{ to: "/gig/6", label: "Start Step 6" }}
      />
    );
  }

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view && loaded.data ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <RunItScreen
            view={view}
            clientChecklist={loaded.data[1].clientChecklist}
            busy={busy}
            onChange={saveSoon}
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completeRunIt());
                if (!wasDone) setFinished(true);
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

export function GrowRoute() {
  const loaded = useAsync(fetchGrow, []);
  const [view, setView] = useState<GrowView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (loaded.data) setView(loaded.data);
  }, [loaded.data]);

  const saveSoon = useDebouncedCallback((budget: Budget, plan: GrowthPlan) => {
    void saveOrQueue("grow", "grow", { budget, plan }, async (value) =>
      setView(await saveGrow(value.budget, value.plan)),
    ).catch((e: unknown) => setError(messageOf(e)));
  }, 400);

  if (finished) {
    return (
      <DoneScreen
        kicker="Pathway complete"
        title="You've completed the Growth Pathway."
        sub="You're Work-Enabled, you've run live client work, and you have a plan to keep and grow what you earn. If you're on the finance track, your handover to your provider is now open. The optional Grow Library is open too."
        next={{ to: "/fin/6", label: "Go to finance handover" }}
      />
    );
  }

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <GrowScreen
            view={view}
            busy={busy}
            onChange={saveSoon}
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completeGrow());
                if (!wasDone) setFinished(true);
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

export function GrowLibraryRoute() {
  const loaded = useAsync(fetchGrowLibrary, []);
  const [library, setLibrary] = useState<GrowLibrary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setLibrary(loaded.data);
  }, [loaded.data]);

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {library ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <GrowLibraryScreen
            library={library}
            onToggle={(key) =>
              void attempt(
                () => undefined,
                setError,
                async () => setLibrary(await toggleLibraryModule(key)),
              )
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}
